import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';

import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { FormModalComponent } from '../../../modals/form-modal/form-modal.component';
import {
  Patient_Details,
  VC_CommunicationMessage,
  VcPatientListItem,
  ViewContent,
} from '../../../models/view-content.models/view-content.model';
import { FormioRendererI18n } from '../../data-interaction/formio-renderer/formio-renderer.component';
import { OrgaTabsTasksComponent } from '../orga-tabs-tasks/orga-tabs-tasks.component';

interface Tab {
  id: string;
  label: string;
}

@Component({
  selector: 'app-workflow-tabs',
  templateUrl: './workflow-tabs.component.html',
  styleUrls: ['./workflow-tabs.component.scss'],
  standalone: true,
  imports: [TranslateModule, CommonModule, FormsModule, OrgaTabsTasksComponent, MatCheckboxModule, MatIcon],
})
export class WorkflowTabsComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  @Input() patientDetails: Patient_Details | undefined;
  @Input() tasks: VC_CommunicationMessage[] = [];

  filteredTasks: VC_CommunicationMessage[] = [];
  showOnlyTodayTasks: boolean = false;

  @Input() workflowDataPatients: VcPatientListItem | undefined;
  @Input() workflowDataPatientsDetails: Patient_Details[] = [];

  @Input() selectedToggle: string | null | undefined = 'tasks';
  @Input() viewContentI18n: FormioRendererI18n | undefined;

  @Output() selectedIcon: EventEmitter<number> = new EventEmitter<number>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() viewContentChange = new EventEmitter<ViewContent<unknown>>();

  tabs: Tab[] = [
    {
      id: 'tasks',
      label: 'COMPONENT.PAGE_ORGA.tasks',
    },
    {
      id: 'medical_orders',
      label: 'COMPONENT.PAGE_ORGA.medical_orders',
    },
    {
      id: 'diagnostic_findings',
      label: 'COMPONENT.PAGE_ORGA.diagnostic_findings',
    },
  ];

  constructor(
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.selectedToggle) {
      this.selectedToggle = 'tasks';
    }
    this.filteredTasks = [...this.tasks];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tasks']) {
      this.filterTasksByDate();
    }
  }

  filterTasksByDate(): void {
    if (this.showOnlyTodayTasks) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      this.filteredTasks = this.tasks.filter(task => {
        // Check if goalDateOn is today
        if (task.data && task.data.goalDateOn) {
          const taskDate = new Date(task.data.goalDateOn);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate.getTime() === today.getTime();
        }
        return false;
      });
    } else {
      // If checkbox is unchecked, show all tasks
      this.filteredTasks = [...this.tasks];
    }
  }

  public async onClickOnTask($event: VC_CommunicationMessage): Promise<void> {
    const dialogRef = this.dialog.open(FormModalComponent, {
      data: {
        form_data: { data: $event.data },
        form_file_name: 'form_tasks.json',
        history: $event.history,
        viewContentI18n: $event.i18n,
        vcPatientDetails: this.patientDetails,
      },
      maxHeight: 'calc(100vh - 16px)',
      panelClass: 'patient-overview-dialog-container',
    });

    const res = await firstValueFrom(dialogRef.afterClosed());

    if (!res || res.role === 'cancel') return;

    const d = $event.data;
    d.done = res.data.find((e: { key: string; value: any }) => e.key === 'done')?.value;
    d.editor = res.data.find((e: { key: string; value: any }) => e.key === 'editor')?.value;
    d.details = res.data.find((e: { key: string; value: any }) => e.key === 'details')?.value;
    d.priority = res.data.find((e: { key: string; value: any }) => e.key === 'priority')?.value;
    d.task_name = res.data.find((e: { key: string; value: any }) => e.key === 'task_name')?.value;
    d.contractor = res.data.find((e: { key: string; value: any }) => e.key === 'contractor')?.value;
    d.goalDateOn = res.data.find((e: { key: string; value: any }) => e.key === 'goalDateOn')?.value;

    this.viewContentChange.emit($event);
  }

  public selectTab(tabId: string): void {
    this.ensureSelection(tabId);
  }

  public ensureSelection(tabValue: string): void {
    if (this.selectedToggle !== tabValue) {
      this.selectedToggle = tabValue;
      this.selectionChange.emit({
        source: null as any,
        selected: true,
        value: tabValue,
        isUserInput: true,
      });
      this.cdr.detectChanges();
    }
  }

  onChipSelectChange(event: any): void {
    if (event.selected) {
      this.selectionChange.emit(event);
    } else {
      this.ensureSelection((this.selectedToggle as string) || 'tasks');
    }
  }
}
